import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { CATEGORIES, getAllProducts, getCategory, getSubcategories, normalize } from '../data/catalog';
import ProductCard from '../components/ProductCard';
import { isProductVisible, useAvailabilityTick } from '../utils/availability';

function FilterPanel({ colors, usages, colorSel, setColorSel, usageSel, setUsageSel }) {
  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  return (
    <div className="space-y-7">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Cor</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {colors.map((c) => (
            <button
              key={c}
              onClick={() => toggle(colorSel, setColorSel, c)}
              data-testid={`filter-color-${normalize(c).replace(/\s+/g, '-')}`}
              aria-pressed={colorSel.includes(c)}
              className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-colors ${
                colorSel.includes(c)
                  ? 'border-navy bg-navy text-white'
                  : 'border-navy/20 text-foreground/70 hover:border-navy'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Finalidade</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {usages.map((u) => (
            <button
              key={u}
              onClick={() => toggle(usageSel, setUsageSel, u)}
              data-testid={`filter-usage-${normalize(u).replace(/\s+/g, '-')}`}
              aria-pressed={usageSel.includes(u)}
              className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-colors ${
                usageSel.includes(u)
                  ? 'border-navy bg-navy text-white'
                  : 'border-navy/20 text-foreground/70 hover:border-navy'
              }`}
            >
              {u}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CategoryPage() {
  const { slug } = useParams();
  const isAll = slug === 'todos';
  const category = getCategory(slug);
  const [colorSel, setColorSel] = useState([]);
  const [usageSel, setUsageSel] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  useAvailabilityTick();
  const [searchParams, setSearchParams] = useSearchParams();
  const subs = isAll ? [] : getSubcategories(slug);
  const activeSub = subs.some((s) => s.slug === searchParams.get('sub')) ? searchParams.get('sub') : '';

  const base = useMemo(
    () => (isAll ? getAllProducts() : getAllProducts().filter((p) => p.categorySlug === slug)).filter(isProductVisible),
    [isAll, slug]
  );

  const colors = useMemo(
    () => [...new Set(base.flatMap((p) => p.colorTags))].sort(),
    [base]
  );
  const usages = useMemo(
    () => [...new Set(base.flatMap((p) => p.usageTags))].sort(),
    [base]
  );

  const filtered = useMemo(
    () =>
      base.filter(
        (p) =>
          (colorSel.length === 0 || colorSel.some((c) => p.colorTags.includes(c))) &&
          (usageSel.length === 0 || usageSel.some((u) => p.usageTags.includes(u))) &&
          (activeSub === '' || p.subcategorySlug === activeSub)
      ),
    [base, colorSel, usageSel, activeSub]
  );

  if (!isAll && !category) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-20 text-center">
        <h1 className="font-serif text-3xl text-navy">Categoria não encontrada</h1>
      </main>
    );
  }

  const sub = subs.find((s) => s.slug === activeSub) || null;
  const title = isAll ? 'Todos os Tecidos' : sub ? `${category.name} · ${sub.name}` : category.name;
  const description = isAll
    ? 'Explore nosso acervo completo de tecidos para moda, decoração e artesanato.'
    : category.description;

  return (
    <main className="mx-auto max-w-7xl px-4 py-8 md:py-12" data-testid="category-page">
      <nav className="text-xs text-muted-foreground" aria-label="Breadcrumb">
        Início / <span className="text-foreground">{title}</span>
      </nav>
      <div className="mt-3 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl tracking-tight text-navy md:text-4xl" data-testid="category-title">{title}</h1>
          <p className="mt-2 max-w-xl text-sm text-muted-foreground md:text-base">{description}</p>
        </div>
        <button
          className="flex items-center gap-2 rounded-full border border-navy/20 px-4 py-2.5 text-sm font-semibold text-navy md:hidden"
          onClick={() => setFiltersOpen(true)}
          data-testid="filters-open-button"
        >
          <SlidersHorizontal className="h-4 w-4" /> Filtrar
        </button>
      </div>

      {subs.length > 0 && (
        <div className="no-scrollbar -mx-4 mt-5 flex gap-2 overflow-x-auto px-4" data-testid="subcategory-filters" role="group" aria-label="Subcategorias">
          <button
            type="button"
            onClick={() => setSearchParams({})}
            aria-pressed={activeSub === ''}
            data-testid="subfilter-all"
            className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
              activeSub === '' ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
            }`}
          >
            Todas
          </button>
          {subs.map((s) => (
            <button
              key={s.slug}
              type="button"
              onClick={() => setSearchParams({ sub: s.slug })}
              aria-pressed={activeSub === s.slug}
              data-testid={`subfilter-${s.slug}`}
              className={`shrink-0 rounded-full border px-4 py-2.5 text-xs font-semibold transition-colors ${
                activeSub === s.slug ? 'border-navy bg-navy text-white' : 'border-navy/20 text-foreground/70 hover:border-navy'
              }`}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 grid gap-10 md:grid-cols-[220px_1fr]">
        <aside className="hidden md:block" data-testid="filters-sidebar">
          <FilterPanel
            colors={colors}
            usages={usages}
            colorSel={colorSel}
            setColorSel={setColorSel}
            usageSel={usageSel}
            setUsageSel={setUsageSel}
          />
        </aside>

        <div>
          <p className="mb-4 text-sm text-muted-foreground" data-testid="category-result-count">
            {filtered.length} {filtered.length === 1 ? 'tecido encontrado' : 'tecidos encontrados'}
          </p>
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-navy/20 bg-white p-12 text-center" data-testid="category-empty-state">
              <p className="font-serif text-xl text-navy">Nenhum tecido encontrado</p>
              <p className="mt-2 text-sm text-muted-foreground">Ajuste os filtros ou fale conosco pelo WhatsApp.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 lg:grid-cols-3 xl:grid-cols-4" data-testid="category-product-grid">
              {filtered.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>

      {filtersOpen && (
        <div className="fixed inset-0 z-[80] md:hidden" data-testid="filters-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setFiltersOpen(false)} />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white p-6">
            <div className="flex items-center justify-between">
              <p className="font-serif text-xl text-navy">Filtros</p>
              <button onClick={() => setFiltersOpen(false)} aria-label="Fechar filtros" data-testid="filters-close-button" className="rounded-full p-2 hover:bg-navy-light">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4">
              <FilterPanel
                colors={colors}
                usages={usages}
                colorSel={colorSel}
                setColorSel={setColorSel}
                usageSel={usageSel}
                setUsageSel={setUsageSel}
              />
            </div>
            <button
              onClick={() => setFiltersOpen(false)}
              className="mt-8 w-full rounded-full bg-navy py-3.5 text-sm font-semibold text-white"
              data-testid="filters-apply-button"
            >
              VER {filtered.length} {filtered.length === 1 ? 'TECIDO' : 'TECIDOS'}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
